import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export const AuthDialog = ({ open, onClose, redirectTo = "/universe" }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      onClose();
      navigate(redirectTo);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#05070D]/80 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="auth-dialog-overlay">
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="glass-strong hud-corners scanlines relative w-full max-w-md p-8 sm:p-10" data-testid="auth-dialog">
            <button onClick={onClose} className="absolute right-4 top-4 text-slate-500 hover:text-cyan-300 transition-colors" data-testid="auth-close-button">
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2 label-mono mb-3">
              <Lock className="h-3 w-3 text-cyan-400" /> Airlock access terminal
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-50 mb-6">
              {mode === "login" ? "Identify yourself" : "Register a new explorer"}
            </h2>

            <div className="flex gap-1 mb-6 p-1 rounded-full bg-white/5 w-fit">
              {["login", "register"].map((m) => (
                <button key={m} type="button" onClick={() => { setMode(m); setError(""); }}
                  data-testid={`auth-mode-${m}`}
                  className={`font-mono text-[0.65rem] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full transition-colors duration-200 ${
                    mode === m ? "bg-cyan-400 text-[#05070D]" : "text-slate-400 hover:text-slate-100"}`}>
                  {m === "login" ? "Sign in" : "Register"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="label-mono block mb-2">Callsign</label>
                  <input className="input-hud" value={form.name} onChange={set("name")} placeholder="Nova Reyes" required minLength={2} data-testid="register-name-input" />
                </div>
              )}
              <div>
                <label className="label-mono block mb-2">Email</label>
                <input type="email" className="input-hud" value={form.email} onChange={set("email")} placeholder="you@station.net" required
                  data-testid={mode === "login" ? "login-email-input" : "register-email-input"} />
              </div>
              <div>
                <label className="label-mono block mb-2">Passphrase</label>
                <input type="password" className="input-hud" value={form.password} onChange={set("password")} placeholder="••••••••" required minLength={6}
                  data-testid={mode === "login" ? "login-password-input" : "register-password-input"} />
              </div>
              {error && <p className="text-sm text-[#FF5500] font-mono" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={busy} className="btn-primary w-full mt-2"
                data-testid={mode === "login" ? "login-submit-button" : "register-submit-button"}>
                {busy ? "Authenticating…" : mode === "login" ? "Open airlock" : "Enroll & enter"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
